/**
 * DataHealthList — listado tabular REUSABLE para el panel Data Health / Config UI.
 *
 * Unifica en UN solo componente los listados tabulares de Data Health (OCULTOS,
 * COLA DE REVISIÓN, DUDOSOS), Data Issues y Usuarios, que antes tenían cada uno
 * su propia <table> con styling divergente. Decisión de Gabriel (2026-08-08):
 * "las diferencias no justifican tener componentes separados con styling
 * diferente".
 *
 * Se configura por caso de uso vía `columns` (config declarativa por columna) y
 * un flag opcional de selección múltiple (`selection`). El lenguaje visual sale
 * del estilo de OCULTOS (px-2 py-1, text-[11px], header sticky gris, hover
 * gris, fila activa azul).
 *
 * Motor: @tanstack/react-table para column sizing + dnd-kit para reorder de
 * columnas (mismo stack que src/components/DataTable.tsx). Ambas prefs
 * (anchos + orden) PERSISTEN por usuario y por `listId` en Firestore
 * (`users/{uid}/settings/preferences` → `listPreferences.{listId}`), vía
 * src/services/userPreferences.ts.
 *
 * Reglas de diseño:
 *  - `table-layout: fixed` + anchos FIJOS por columna (de TanStack columnSizing)
 *    → los anchos NO varían según el contenido al navegar el listado.
 *  - Anchos REDIMENSIONABLES: arrastrar el borde derecho del header (simil
 *    Excel). Al soltar se persiste el ancho por `listId`.
 *  - Orden REORDENABLE: arrastrar el header (dnd-kit). Al soltar se persiste el
 *    orden por `listId`.
 *  - Sort del servidor: si el caller pasa `onSort`, el click en el header cicla
 *    asc→desc→(none) y llama a `onSort(key, dir)` para que el PADRE refetchee
 *    desde el server. DataHealthList NO ordena localmente — solo refleja el
 *    indicador. Sin `onSort` no hay UI de sort (comportamiento previo).
 *  - TableNavigation va ARRIBA y ABAJO compartiendo el MISMO estado (lo controla
 *    el caller vía props de paginación). Convención de id:
 *    `pag-datahealth-<tab>-<top|bottom>`.
 *  - NO conoce drawers ni modales: solo renderiza el listado exterior + su
 *    paginación. El caller monta su propio detalle por fuera (`overlay`).
 *
 * Doc: docs/design-system/COMPONENT_CATALOG.md (sección "DataHealthList").
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, ChevronUp, ChevronDown, GripVertical } from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  type ColumnSizingState,
  type ColumnOrderState,
} from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RowCheckbox } from '../shared/inspectorTableHelpers';
import TableNavigation from '../../TableNavigation';
import { useAuth } from '../../../contexts/AuthContext';
import { saveListPreferences, loadListPreferences } from '../../../services/userPreferences';

// ----------------------------------------------------------------------------
// Config de columnas
// ----------------------------------------------------------------------------

export interface DataHealthColumn<T> {
  /** Identificador estable de la columna (para React key + persistencia). */
  key: string;
  /** Texto del header. */
  header: React.ReactNode;
  /**
   * Ancho INICIAL de la columna. String CSS (px/rem/%). Se resuelve a px para el
   * motor de sizing de TanStack (rem → *16). Con table-layout:fixed esto fija el
   * ancho independiente del contenido. Si se omite, la columna reparte el
   * sobrante (usar solo en la columna "flexible", típicamente title).
   */
  width?: string;
  /** Alineación del contenido de la celda + header. Default 'left'. */
  align?: 'left' | 'right' | 'center';
  /** Render custom de la celda. Si se omite se muestra `row[key]` como texto. */
  render?: (row: T) => React.ReactNode;
  /** Clases extra para las celdas <td> de esta columna. */
  cellClassName?: string;
  /**
   * Si es `false`, la columna NO es ordenable aunque el listado tenga sort
   * habilitado (`onSort`). Usar para columnas cuyo dato vive en la toma o es
   * derivado (title/sitio/catalog_at): el backend las omite del allow-list para
   * no forzar el JOIN toma antes de paginar. Default: ordenable si hay `onSort`.
   */
  sortable?: boolean;
}

// ----------------------------------------------------------------------------
// Selección múltiple (opcional — solo COLA / DUDOSOS)
// ----------------------------------------------------------------------------

export interface DataHealthSelection {
  /** IDs seleccionados. */
  selected: Set<string>;
  /** true si TODA la página visible está seleccionada (controla el checkbox header). */
  pageAllSelected: boolean;
  /** Toggle "seleccionar toda la página". */
  onTogglePage: (checked: boolean) => void;
  /** Toggle de una fila. */
  onToggleOne: (id: string, checked: boolean) => void;
}

// ----------------------------------------------------------------------------
// Props
// ----------------------------------------------------------------------------

export interface DataHealthListProps<T> {
  /** Config de columnas (sin la columna de checkbox — se agrega sola si hay `selection`). */
  columns: DataHealthColumn<T>[];
  /** Filas a renderizar (la página actual). */
  rows: T[];
  /** Extrae el id estable de una fila (React key + selección + fila activa). */
  getRowId: (row: T) => string;
  /** Click en una fila (abrir detalle). Se ignora si el click cae en checkbox/link/botón. */
  onRowClick?: (row: T) => void;
  /** Id de la fila activa (resaltada en azul). */
  activeRowId?: string | null;
  /** Soporte OPCIONAL de selección múltiple (checkboxes). OCULTOS NO lo pasa. */
  selection?: DataHealthSelection;

  /**
   * Id ESTABLE del listado para persistir prefs (anchos + orden) por usuario.
   * Ej. "datahealth-dudosos", "config-usuarios". Si se omite, las prefs NO
   * persisten (solo estado local — backward compat con callers que no lo pasan).
   */
  listId?: string;

  // ---- Sort del servidor (opcional). Si `onSort` está presente, se habilita
  // la UI de sort en los headers y el ordenamiento lo resuelve el PADRE. ----
  /** Callback disparado al clickear un header. El padre refetchea con estos params. */
  onSort?: (sortBy: string, sortDir: 'asc' | 'desc') => void;
  /** Columna ordenada actualmente (DataHealthColumn.key). Controlado por el padre. */
  sortBy?: string | null;
  /** Dirección de orden actual. Controlado por el padre. */
  sortDir?: 'asc' | 'desc' | null;

  loading?: boolean;
  /** Mensaje/nodo de estado vacío (cuando !loading && rows.length === 0). */
  emptyState?: React.ReactNode;

  // Paginación (compartida top+bottom).
  pagination: {
    /** Prefijo de id para las barras: genera `pag-datahealth-<idPrefix>-top|bottom`. */
    idPrefix: string;
    pageIndex: number;
    pageSize: number;
    totalRows: number;
    totalPages: number;
    onPageChange: (newIndex: number) => void;
    onPageSizeChange: (newSize: number) => void;
  };

  /** Alto mínimo del contenedor de la tabla (px). Default 520. */
  minHeight?: number;
  /** Nodo overlay a montar dentro del contenedor de la tabla (ej. drawer inline/overlay). */
  overlay?: React.ReactNode;
}

const alignClass = (a?: 'left' | 'right' | 'center') =>
  a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';

const SELECT_COL_WIDTH = 32; // 2rem

/** Convierte un `width` CSS de config (px/rem/número) a px numéricos. */
const widthToPx = (w?: string): number | undefined => {
  if (!w) return undefined;
  const s = w.trim();
  if (s.endsWith('rem')) return parseFloat(s) * 16;
  if (s.endsWith('px')) return parseFloat(s);
  const n = parseFloat(s);
  return isNaN(n) ? undefined : n;
};

// ----------------------------------------------------------------------------
// Header arrastrable (dnd-kit) — mirror de SortableClientCard en ClientsPanel.
// ----------------------------------------------------------------------------
interface SortableHeaderProps {
  id: string;
  align?: 'left' | 'right' | 'center';
  sortState: 'asc' | 'desc' | null;
  sortable: boolean;
  onSortClick?: () => void;
  onResizeMouseDown?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  id,
  align,
  sortState,
  sortable,
  onSortClick,
  onResizeMouseDown,
  children,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : 'auto',
  };
  return (
    <th
      ref={setNodeRef}
      style={style}
      className={`group relative px-2 py-1 font-medium border-r border-gray-200 bg-gray-100 ${alignClass(align)}`}
    >
      <div
        className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''}`}
      >
        {/* Zona arrastrable (reorder): grip + label. El grip aparece al hover como pista. */}
        <span
          {...attributes}
          {...listeners}
          className="flex items-center gap-1 min-w-0 cursor-grab active:cursor-grabbing"
          title="Arrastrar para reordenar columna"
        >
          <GripVertical className="w-3 h-3 flex-shrink-0 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          {/* El texto: click ordena (si la columna es ordenable). */}
          <span
            onClick={sortable ? onSortClick : undefined}
            className={`block truncate ${sortable ? 'hover:text-blue-600 cursor-pointer' : ''}`}
          >
            {children}
          </span>
        </span>
        {sortable && (
          <span className="flex-shrink-0 text-blue-600">
            {sortState === 'asc' ? (
              <ChevronUp className="w-3 h-3" />
            ) : sortState === 'desc' ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <span className="w-3 h-3 inline-block" />
            )}
          </span>
        )}
      </div>
      {/* Handle de redimensión: arrastrar el borde derecho (simil Excel). */}
      <span
        role="separator"
        aria-orientation="vertical"
        onMouseDown={onResizeMouseDown}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400/60 select-none touch-none"
      />
    </th>
  );
};

/**
 * Listado tabular reusable de Data Health. Estilo tomado de OCULTOS.
 * Genérico en `T` para tipar filas de distintos casos de uso.
 */
function DataHealthList<T>({
  columns,
  rows,
  getRowId,
  onRowClick,
  activeRowId,
  selection,
  listId,
  onSort,
  sortBy,
  sortDir,
  loading,
  emptyState,
  pagination,
  minHeight = 520,
  overlay,
}: DataHealthListProps<T>) {
  const { idPrefix, pageIndex, pageSize, totalRows, totalPages, onPageChange, onPageSizeChange } =
    pagination;
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? null;

  const hasSelection = !!selection;
  const totalCols = columns.length + (hasSelection ? 1 : 0);
  const sortEnabled = !!onSort;

  // Config de columnas por key (para render de celdas/headers desde el id de TanStack).
  const colByKey = useMemo(() => {
    const m = new Map<string, DataHealthColumn<T>>();
    columns.forEach((c) => m.set(c.key, c));
    return m;
  }, [columns]);

  // ---- TanStack: columnas mínimas. El render real de celdas/headers lo hacemos
  // manualmente abajo (para conservar el markup/estilo de OCULTOS); TanStack se
  // usa como MOTOR de columnSizing + columnOrder. ----
  const columnHelper = useMemo(() => createColumnHelper<T>(), []);
  const tsColumns = useMemo(
    () =>
      columns.map((c) =>
        columnHelper.display({
          id: c.key,
          size: widthToPx(c.width) ?? 160,
          minSize: 40,
        })
      ),
    [columns, columnHelper]
  );

  // ---- Estado de sizing + order (persistido por listId). ----
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => columns.map((c) => c.key));
  const prefsLoadedRef = useRef(false);
  // Ref espejo de columnSizing SIEMPRE fresco: el handler de mouseup del resize se crea
  // en el mousedown y capturaría un columnSizing viejo por closure (bug: al recargar tomaba
  // el ancho guardado ANTES del resize). Persistimos desde el ref, no desde el state cerrado.
  const columnSizingRef = useRef<ColumnSizingState>({});
  useEffect(() => {
    columnSizingRef.current = columnSizing;
  }, [columnSizing]);

  // Al montar (o cambiar listId/uid): cargar prefs persistidas y aplicarlas.
  useEffect(() => {
    prefsLoadedRef.current = false;
    if (!uid || !listId) {
      // Sin persistencia: orden default = orden de config.
      setColumnOrder(columns.map((c) => c.key));
      setColumnSizing({});
      prefsLoadedRef.current = true;
      return;
    }
    let cancelled = false;
    (async () => {
      const prefs = await loadListPreferences(uid, listId);
      if (cancelled) return;
      const validKeys = new Set(columns.map((c) => c.key));
      if (prefs?.order && prefs.order.length) {
        // Orden persistido, filtrando keys que ya no existen y agregando nuevas al final.
        const filtered = prefs.order.filter((k) => validKeys.has(k));
        const missing = columns.map((c) => c.key).filter((k) => !filtered.includes(k));
        setColumnOrder([...filtered, ...missing]);
      } else {
        setColumnOrder(columns.map((c) => c.key));
      }
      if (prefs?.widths) {
        const sizing: ColumnSizingState = {};
        for (const [k, v] of Object.entries(prefs.widths)) {
          if (validKeys.has(k) && typeof v === 'number') sizing[k] = v;
        }
        setColumnSizing(sizing);
      } else {
        setColumnSizing({});
      }
      prefsLoadedRef.current = true;
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, listId, columns]);

  const table = useReactTable({
    data: rows,
    columns: tsColumns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    state: { columnSizing, columnOrder },
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
  });

  const startResize = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    const header = table.getHeaderGroups()[0]?.headers.find((h) => h.id === key);
    if (!header) return;
    const startX = e.clientX;
    const startW = header.getSize();
    let finalW = startW;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(40, startW + (ev.clientX - startX));
      finalW = next;
      setColumnSizing((prev) => {
        const updated = { ...prev, [key]: next };
        columnSizingRef.current = updated; // mantener el ref fresco sincrónicamente
        return updated;
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persistir el ancho FINAL (desde el ref + el último valor de esta columna),
      // no el columnSizing capturado por closure en el mousedown.
      if (uid && listId) {
        void saveListPreferences(uid, listId, {
          widths: { ...columnSizingRef.current, [key]: finalW },
        });
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // ---- dnd-kit: reorder de columnas. ----
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setColumnOrder((prev) => {
      const base = prev.length ? prev : columns.map((c) => c.key);
      const oldIndex = base.indexOf(active.id as string);
      const newIndex = base.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(base, oldIndex, newIndex);
      if (uid && listId) void saveListPreferences(uid, listId, { order: next });
      return next;
    });
  };

  // ---- Sort UI: cicla asc → desc → none al clickear el header. ----
  const handleSortClick = (key: string) => {
    if (!onSort) return;
    // asc → desc → asc (nunca "none" para no dejar el server sin orden explícito).
    const nextDir: 'asc' | 'desc' =
      sortBy === key && sortDir === 'asc' ? 'desc' : 'asc';
    onSort(key, nextDir);
  };

  // Orden final de headers según columnOrder (fallback al orden de config).
  const orderedHeaders = table.getHeaderGroups()[0]?.headers ?? [];

  const navProps = {
    pageIndex,
    pageSize,
    totalRows,
    totalPages,
    onPageChange,
    onPageSizeChange,
  };

  return (
    <div className="flex flex-col">
      {/* Paginación superior — mismo control que Publicaciones (va arriba Y abajo) */}
      <TableNavigation id={`pag-datahealth-${idPrefix}-top`} position="top" {...navProps} />

      {/* Body: tabla + overlay opcional (el overlay se superpone, la lista queda intacta) */}
      <div
        className="relative border border-gray-200 rounded overflow-hidden bg-white"
        style={{ minHeight }}
      >
        <div className="w-full h-full overflow-auto">
          <table className="w-full text-[11px]" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              {hasSelection && <col style={{ width: `${SELECT_COL_WIDTH}px` }} />}
              {orderedHeaders.map((h) => (
                <col key={h.id} style={{ width: `${h.getSize()}px` }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 bg-gray-100 text-gray-700 z-10">
              <tr>
                {hasSelection && (
                  <th className="px-2 py-1 text-left sticky left-0 bg-gray-100 border-r border-gray-200 z-10">
                    <RowCheckbox
                      checked={selection.pageAllSelected}
                      onChange={selection.onTogglePage}
                      ariaLabel="Seleccionar página"
                    />
                  </th>
                )}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={orderedHeaders.map((h) => h.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    {orderedHeaders.map((h) => {
                      const c = colByKey.get(h.id);
                      // Ordenable solo si el listado tiene sort Y la columna no
                      // hace opt-out (sortable !== false). title/sitio/derivadas
                      // se excluyen: el backend las omite del allow-list.
                      const colSortable = sortEnabled && c?.sortable !== false;
                      const state: 'asc' | 'desc' | null =
                        colSortable && sortBy === h.id ? (sortDir ?? null) : null;
                      return (
                        <SortableHeader
                          key={h.id}
                          id={h.id}
                          align={c?.align}
                          sortable={colSortable}
                          sortState={state}
                          onSortClick={() => handleSortClick(h.id)}
                          onResizeMouseDown={(e) => startResize(e, h.id)}
                        >
                          {c?.header ?? h.id}
                        </SortableHeader>
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={totalCols} className="px-3 py-8 text-center text-gray-500">
                    <Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Cargando…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={totalCols} className="px-3 py-8 text-center text-gray-400">
                    {emptyState ?? <span className="italic">Sin items.</span>}
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((row) => {
                  const id = getRowId(row);
                  const isSel = selection?.selected.has(id) ?? false;
                  const isActive = activeRowId != null && activeRowId === id;
                  return (
                    <tr
                      key={id}
                      onClick={(e) => {
                        // ignorar clicks en checkbox / link / botón dentro de la fila
                        if ((e.target as HTMLElement).closest('input, button, a')) return;
                        onRowClick?.(row);
                      }}
                      className={`border-b border-gray-100 ${onRowClick ? 'cursor-pointer' : ''} ${
                        isActive ? 'bg-blue-50' : isSel ? 'bg-blue-50/50' : 'hover:bg-gray-50'
                      }`}
                    >
                      {hasSelection && (
                        <td className="px-2 py-1 sticky left-0 bg-inherit border-r border-gray-200">
                          <RowCheckbox
                            checked={isSel}
                            onChange={(c) => selection.onToggleOne(id, c)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                      )}
                      {orderedHeaders.map((h) => {
                        const c = colByKey.get(h.id);
                        return (
                          <td
                            key={h.id}
                            className={`px-2 py-1 border-r border-gray-200 ${alignClass(c?.align)} ${c?.cellClassName ?? ''}`}
                          >
                            {c?.render
                              ? c.render(row)
                              : ((row as Record<string, unknown>)[h.id] as React.ReactNode)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {overlay}
      </div>

      {/* Paginación inferior — mismo control que Publicaciones (TableNavigation) */}
      <div className="mt-2">
        <TableNavigation id={`pag-datahealth-${idPrefix}-bottom`} position="bottom" {...navProps} />
      </div>
    </div>
  );
}

export default DataHealthList;
